/**
 * Shape catalogue + geometry — the single source of truth for which shapes exist
 * and where their outlines run.
 *
 * Two kinds of shape:
 *  - 'box'  — rect / ellipse / line. CSS draws these from the element box alone
 *             (border-radius, 50%), so they keep the original box-based paths in
 *             ElementRenderer and the export.
 *  - 'path' — everything else (triangle, polygon, star, arrow, …). Defined as a
 *             point list in a 0..1 unit box, scaled to the element and turned
 *             into ONE command list that the editor replays as an SVG `d` and
 *             the export replays onto Canvas2D. Same commands both sides, so the
 *             PNG/PDF can't drift from what the canvas shows.
 *
 * Adding a shape = one entry in SHAPE_DEFS. Menus, layer icons, the parameter
 * controls, both renderers and the export all read it from here.
 *
 * Parametric shapes (Figma's polygon sides, star points, …) keep their values on
 * the ELEMENT, not in styles — they are geometry, like width and height.
 */

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/* ------------------------------------------------------------------ *
 *  Parameters a shape can expose (rendered by the properties panel)
 * ------------------------------------------------------------------ */

export const SHAPE_PARAM_DEFS = {
    sides: { label: 'Sides', min: 3, max: 24, step: 1 },
    starPoints: { label: 'Points', min: 3, max: 24, step: 1 },
    innerRadius: { label: 'Inner radius', min: 1, max: 99, step: 1, suffix: '%' },
    headSize: { label: 'Head', min: 5, max: 95, step: 1, suffix: '%' },
    tailSize: { label: 'Thickness', min: 5, max: 100, step: 1, suffix: '%' },
    slant: { label: 'Slant', min: 0, max: 49, step: 1, suffix: '%' },
    thickness: { label: 'Thickness', min: 5, max: 95, step: 1, suffix: '%' },
    tailWidth: { label: 'Tail', min: 5, max: 60, step: 1, suffix: '%' },
};

/* ------------------------------------------------------------------ *
 *  Unit-box point builders (0..1 on both axes)
 * ------------------------------------------------------------------ */

/**
 * Stretch a point list so it exactly fills the unit box. Figma normalises the
 * same way: a 3-sided polygon fills its frame as a full-height triangle rather
 * than sitting inside the circle it was generated from.
 */
function normalizePoints(pts) {
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const spanX = Math.max(...xs) - minX || 1;
    const spanY = Math.max(...ys) - minY || 1;
    return pts.map(([x, y]) => [(x - minX) / spanX, (y - minY) / spanY]);
}

/** Regular n-gon, first vertex straight up (Figma's polygon orientation). */
function regularPolygon(sides) {
    const n = clamp(Math.round(Number(sides) || 3), 3, 60);
    const pts = [];
    for (let i = 0; i < n; i += 1) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        pts.push([0.5 + Math.cos(a) / 2, 0.5 + Math.sin(a) / 2]);
    }
    return normalizePoints(pts);
}

/** n-pointed star, first point straight up; `innerPct` = inner/outer radius. */
function starPolygon(points, innerPct) {
    const n = clamp(Math.round(Number(points) || 5), 3, 60);
    const inner = clamp(Number(innerPct) || 38, 1, 99) / 100;
    const pts = [];
    for (let i = 0; i < n * 2; i += 1) {
        const r = (i % 2 ? inner : 1) / 2;
        const a = -Math.PI / 2 + (i * Math.PI) / n;
        pts.push([0.5 + Math.cos(a) * r, 0.5 + Math.sin(a) * r]);
    }
    return normalizePoints(pts);
}

const pct = (v, lo, hi) => clamp(Number(v), lo, hi) / 100;

/* ------------------------------------------------------------------ *
 *  The catalogue
 * ------------------------------------------------------------------ */

/** The hand-drawn shape. Its geometry lives on the element (see `elementPath`). */
export const FREEFORM_SHAPE = 'freeform';

export const SHAPE_GROUPS = [
    { id: 'basic', label: 'Basic' },
    { id: 'polygon', label: 'Polygons' },
    { id: 'arrow', label: 'Arrows' },
    { id: 'other', label: 'Other' },
];

export const SHAPE_DEFS = [
    // --- box shapes: drawn from the element box by CSS / roundRect -----------
    {
        key: 'rect', label: 'Rectangle', kind: 'box', group: 'basic',
        shortcut: 'R', width: 100, height: 100, radius: 8,
    },
    {
        key: 'ellipse', label: 'Ellipse', kind: 'box', group: 'basic',
        shortcut: 'O', width: 100, height: 100,
    },
    {
        key: 'line', label: 'Line', kind: 'box', group: 'basic',
        shortcut: 'L', width: 220, height: 4,
    },

    // --- polygons -----------------------------------------------------------
    {
        key: 'triangle', label: 'Triangle', kind: 'path', group: 'polygon',
        width: 110, height: 100, points: () => regularPolygon(3),
    },
    {
        key: 'rightTriangle', label: 'Right triangle', kind: 'path', group: 'polygon',
        width: 110, height: 100, points: () => [[0, 0], [0, 1], [1, 1]],
    },
    {
        key: 'diamond', label: 'Diamond', kind: 'path', group: 'polygon',
        width: 100, height: 100, points: () => regularPolygon(4),
    },
    {
        key: 'pentagon', label: 'Pentagon', kind: 'path', group: 'polygon',
        width: 100, height: 100, points: () => regularPolygon(5),
    },
    {
        key: 'hexagon', label: 'Hexagon', kind: 'path', group: 'polygon',
        width: 110, height: 100, points: () => regularPolygon(6),
    },
    {
        key: 'octagon', label: 'Octagon', kind: 'path', group: 'polygon',
        width: 100, height: 100, points: () => regularPolygon(8),
    },
    {
        // Figma's parametric polygon — any side count, editable after insert.
        key: 'polygon', label: 'Polygon', kind: 'path', group: 'polygon',
        width: 100, height: 100,
        params: { sides: 6 },
        points: (p) => regularPolygon(p.sides),
    },
    {
        key: 'star', label: 'Star', kind: 'path', group: 'polygon',
        width: 110, height: 105,
        params: { starPoints: 5, innerRadius: 38 },
        points: (p) => starPolygon(p.starPoints, p.innerRadius),
    },

    // --- arrows -------------------------------------------------------------
    {
        key: 'arrow', label: 'Arrow', kind: 'path', group: 'arrow',
        shortcut: '⇧L', width: 180, height: 90,
        params: { headSize: 45, tailSize: 40 },
        points: (p) => {
            const hd = pct(p.headSize, 5, 95);
            const t = pct(p.tailSize, 5, 100) / 2;
            return [
                [0, 0.5 - t], [1 - hd, 0.5 - t], [1 - hd, 0],
                [1, 0.5],
                [1 - hd, 1], [1 - hd, 0.5 + t], [0, 0.5 + t],
            ];
        },
    },
    {
        key: 'arrowDouble', label: 'Double arrow', kind: 'path', group: 'arrow',
        width: 200, height: 90,
        params: { headSize: 30, tailSize: 40 },
        points: (p) => {
            const hd = pct(p.headSize, 5, 45);
            const t = pct(p.tailSize, 5, 100) / 2;
            return [
                [0, 0.5], [hd, 0], [hd, 0.5 - t],
                [1 - hd, 0.5 - t], [1 - hd, 0], [1, 0.5], [1 - hd, 1], [1 - hd, 0.5 + t],
                [hd, 0.5 + t], [hd, 1],
            ];
        },
    },
    {
        key: 'chevron', label: 'Chevron', kind: 'path', group: 'arrow',
        width: 120, height: 100,
        params: { headSize: 35 },
        points: (p) => {
            const hd = pct(p.headSize, 5, 60);
            return [[0, 0], [1 - hd, 0], [1, 0.5], [1 - hd, 1], [0, 1], [hd, 0.5]];
        },
    },

    // --- other --------------------------------------------------------------
    {
        key: 'parallelogram', label: 'Parallelogram', kind: 'path', group: 'other',
        width: 160, height: 100,
        params: { slant: 20 },
        points: (p) => {
            const s = pct(p.slant, 0, 49);
            return [[s, 0], [1, 0], [1 - s, 1], [0, 1]];
        },
    },
    {
        key: 'trapezoid', label: 'Trapezoid', kind: 'path', group: 'other',
        width: 160, height: 100,
        params: { slant: 22 },
        points: (p) => {
            const s = pct(p.slant, 0, 49);
            return [[s, 0], [1 - s, 0], [1, 1], [0, 1]];
        },
    },
    {
        key: 'cross', label: 'Cross', kind: 'path', group: 'other',
        width: 100, height: 100,
        params: { thickness: 34 },
        points: (p) => {
            const a = (1 - pct(p.thickness, 5, 95)) / 2;
            return [
                [a, 0], [1 - a, 0], [1 - a, a], [1, a], [1, 1 - a], [1 - a, 1 - a],
                [1 - a, 1], [a, 1], [a, 1 - a], [0, 1 - a], [0, a], [a, a],
            ];
        },
    },
    {
        key: 'bubble', label: 'Speech bubble', kind: 'path', group: 'other',
        width: 200, height: 140, radius: 12,
        params: { tailSize: 22, tailWidth: 16 },
        points: (p) => {
            // Body occupies the top (1 - tail) of the box; the tail hangs below it.
            const t = pct(p.tailSize, 5, 60);
            const wSpan = pct(p.tailWidth, 5, 60);
            const body = 1 - t;
            return [
                [0, 0], [1, 0], [1, body],
                [0.28 + wSpan, body], [0.24, 1], [0.24, body],
            ];
        },
    },
    /**
     * Drawn by hand with the pen or the pencil — the one shape whose outline is
     * not generated from parameters but stored on the ELEMENT, in `path`.
     * `hidden` keeps it out of the shape pickers: you get one by drawing it, and
     * turning a rectangle into it would produce a shape with no outline at all.
     */
    {
        key: FREEFORM_SHAPE, label: 'Vector path', kind: 'path', group: 'other',
        width: 160, height: 160, hidden: true,
        points: () => [[0, 0], [1, 0], [1, 1], [0, 1]],
    },
];

const BY_KEY = new Map(SHAPE_DEFS.map((d) => [d.key, d]));

export const DEFAULT_SHAPE = 'rect';

export function getShapeDef(shape) {
    return BY_KEY.get(shape) || BY_KEY.get(DEFAULT_SHAPE);
}

export function isPathShape(shape) {
    return getShapeDef(shape).kind === 'path';
}

/** Shapes whose silhouette is NOT the element box — they need path rendering. */
export function isPathShapeElement(el) {
    return !!el && el.type === 'shape' && isPathShape(el.shape);
}

/** Shape entries of one menu group, in catalogue order. */
export function shapesInGroup(groupId) {
    return SHAPE_DEFS.filter((d) => d.group === groupId && !d.hidden);
}

/** Parameter values for an element, catalogue defaults filling the gaps. */
export function resolveShapeParams(el) {
    const def = getShapeDef(el?.shape);
    const out = { ...(def.params || {}) };
    Object.keys(out).forEach((prop) => {
        const v = Number(el?.[prop]);
        if (Number.isFinite(v)) out[prop] = v;
    });
    return out;
}

/** Panel field descriptors for a shape's parameters ([] when it has none). */
export function shapeParamFields(shape) {
    const def = getShapeDef(shape);
    return Object.keys(def.params || {}).map((prop) => ({
        prop,
        ...SHAPE_PARAM_DEFS[prop],
        default: def.params[prop],
    }));
}

/**
 * Element fields for a fresh shape (or for switching an existing one over):
 * name, box size, its parameters and its default corner radius.
 */
export function shapeElementDefaults(shape) {
    const def = getShapeDef(shape);
    return {
        shape: def.key,
        name: def.label,
        width: def.width,
        height: def.height,
        radius: def.radius || 0,
        params: { ...(def.params || {}) },
    };
}

/* ------------------------------------------------------------------ *
 *  Geometry → drawing commands
 * ------------------------------------------------------------------ */

const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);

/** Point `len` along the segment from `from` towards `to`. */
function toward(from, to, len) {
    const d = dist(from, to) || 1;
    const k = len / d;
    return [from[0] + (to[0] - from[0]) * k, from[1] + (to[1] - from[1]) * k];
}

/**
 * Point list → drawing commands, corners rounded by `radius`.
 *
 * Rounding uses a quadratic curve with the vertex as control point rather than a
 * true arc: an SVG `d` string and a Canvas2D replay then describe the SAME curve
 * (`Q` ↔ quadraticCurveTo), which an arc could not guarantee — SVG's `A` needs a
 * sweep flag and canvas' `arcTo` a tangent solve, and the two disagree by a
 * fraction of a pixel on shallow corners.
 */
export function pathCommands(points, radius = 0) {
    const n = points.length;
    if (n < 3) return [];
    const r = Math.max(0, Number(radius) || 0);
    if (!r) {
        return [
            { c: 'M', x: points[0][0], y: points[0][1] },
            ...points.slice(1).map(([x, y]) => ({ c: 'L', x, y })),
            { c: 'Z' },
        ];
    }
    const cmds = [];
    for (let i = 0; i < n; i += 1) {
        const prev = points[(i - 1 + n) % n];
        const cur = points[i];
        const next = points[(i + 1) % n];
        // Half of each adjoining edge is the most one corner may eat, so
        // neighbouring corners can never overlap however large the radius is.
        const rr = Math.min(r, dist(prev, cur) / 2, dist(cur, next) / 2);
        const a = toward(cur, prev, rr);
        const b = toward(cur, next, rr);
        cmds.push({ c: i === 0 ? 'M' : 'L', x: a[0], y: a[1] });
        cmds.push({ c: 'Q', cx: cur[0], cy: cur[1], x: b[0], y: b[1] });
    }
    cmds.push({ c: 'Z' });
    return cmds;
}

/** Corner radius actually applied, clamped so it can't fold the shape inside out. */
export function shapeCornerRadius(el, w, h) {
    const r = Number(el?.styles?.borderRadius) || 0;
    return clamp(r, 0, Math.min(w, h) / 2);
}

/**
 * A hand-drawn outline, as stored on the element:
 *
 *   path: { closed: bool, nodes: [{ x, y, hIn?: [x, y], hOut?: [x, y] }] }
 *
 * Every coordinate is in the UNIT box (0..1), like the catalogue's generated
 * shapes — so a drawn path scales with its element box for free, and one
 * resize handler covers every shape there is. `hIn` / `hOut` are the Bézier
 * control points either side of a node, absolute in the same unit space (not
 * deltas), which keeps the maths on both renderers to one multiply.
 *
 * A node with no handles joins with a straight line, so the pencil (which only
 * ever emits corners) and the pen (which emits both) share one representation.
 */
export function elementPath(el) {
    const path = el?.path;
    const nodes = Array.isArray(path?.nodes) ? path.nodes.filter((n) => n && Number.isFinite(n.x) && Number.isFinite(n.y)) : [];
    return nodes.length ? { closed: !!path.closed, nodes } : null;
}

export function isFreeformElement(el) {
    return !!el && el.type === 'shape' && el.shape === FREEFORM_SHAPE;
}

/** Commands for a drawn path, scaled from the unit box onto (w, h). */
export function freeformCommands(path, w, h) {
    const { closed, nodes } = path;
    if (nodes.length < 2) return [];
    const at = (n) => [n.x * w, n.y * h];
    const ctrl = (handle, fallback) => (Array.isArray(handle)
        ? [handle[0] * w, handle[1] * h]
        : fallback);

    const cmds = [{ c: 'M', x: nodes[0].x * w, y: nodes[0].y * h }];
    const seg = (a, b) => {
        const from = at(a);
        const to = at(b);
        const c1 = ctrl(a.hOut, null);
        const c2 = ctrl(b.hIn, null);
        if (!c1 && !c2) { cmds.push({ c: 'L', x: to[0], y: to[1] }); return; }
        // One handle missing means that end is a corner — its control point sits
        // on the node itself, which is exactly what "no handle" draws.
        const p1 = c1 || from;
        const p2 = c2 || to;
        cmds.push({
            c: 'C', c1x: p1[0], c1y: p1[1], c2x: p2[0], c2y: p2[1], x: to[0], y: to[1],
        });
    };
    for (let i = 1; i < nodes.length; i += 1) seg(nodes[i - 1], nodes[i]);
    if (closed) {
        seg(nodes[nodes.length - 1], nodes[0]);
        cmds.push({ c: 'Z' });
    }
    return cmds;
}

/**
 * Commands for a path shape, in element-local coordinates (0,0 = top-left of the
 * element box). Empty for box shapes — those are drawn from the box itself.
 */
export function shapeCommands(el, w = el?.width, h = el?.height) {
    const def = getShapeDef(el?.shape);
    if (def.kind !== 'path' || !(w > 0) || !(h > 0)) return [];
    if (el?.shape === FREEFORM_SHAPE) {
        const path = elementPath(el);
        return path ? freeformCommands(path, w, h) : [];
    }
    const pts = def.points(resolveShapeParams(el)).map(([px, py]) => [px * w, py * h]);
    return pathCommands(pts, shapeCornerRadius(el, w, h));
}

const round3 = (v) => Math.round(v * 1000) / 1000;

/** Commands → SVG path data, offset by (ox, oy). */
export function commandsToPathD(cmds, ox = 0, oy = 0) {
    return cmds.map((cmd) => {
        if (cmd.c === 'Z') return 'Z';
        if (cmd.c === 'Q') {
            return `Q${round3(cmd.cx + ox)} ${round3(cmd.cy + oy)} ${round3(cmd.x + ox)} ${round3(cmd.y + oy)}`;
        }
        if (cmd.c === 'C') {
            return `C${round3(cmd.c1x + ox)} ${round3(cmd.c1y + oy)} `
                + `${round3(cmd.c2x + ox)} ${round3(cmd.c2y + oy)} `
                + `${round3(cmd.x + ox)} ${round3(cmd.y + oy)}`;
        }
        return `${cmd.c}${round3(cmd.x + ox)} ${round3(cmd.y + oy)}`;
    }).join('');
}

/** SVG path data for a path shape; '' for box shapes. */
export function shapePathD(el, w = el?.width, h = el?.height, ox = 0, oy = 0) {
    const cmds = shapeCommands(el, w, h);
    return cmds.length ? commandsToPathD(cmds, ox, oy) : '';
}

/**
 * Replay a path shape onto a Canvas2D context at (x, y).
 * `append` keeps the current path (for even-odd holes); otherwise the path is
 * started fresh. Returns false when the element isn't a path shape.
 */
export function traceShapePath(ctx, el, x, y, w, h, { append = false } = {}) {
    const cmds = shapeCommands(el, w, h);
    if (!cmds.length) return false;
    if (!append) ctx.beginPath();
    cmds.forEach((cmd) => {
        if (cmd.c === 'M') ctx.moveTo(cmd.x + x, cmd.y + y);
        else if (cmd.c === 'L') ctx.lineTo(cmd.x + x, cmd.y + y);
        else if (cmd.c === 'Q') ctx.quadraticCurveTo(cmd.cx + x, cmd.cy + y, cmd.x + x, cmd.y + y);
        else if (cmd.c === 'C') {
            ctx.bezierCurveTo(
                cmd.c1x + x, cmd.c1y + y, cmd.c2x + x, cmd.c2y + y, cmd.x + x, cmd.y + y,
            );
        } else ctx.closePath();
    });
    return true;
}

/** Dash pattern shared by the editor's SVG stroke and the export's canvas stroke. */
export function strokeDashArray(style) {
    if (style === 'dashed') return [8, 6];
    if (style === 'dotted') return [2, 4];
    return [];
}
